import React, { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import api from "@/services/api";
import { FiUser, FiMail, FiPhone, FiFileText, FiMessageSquare, FiLinkedin, FiGlobe, FiDollarSign, FiCalendar, FiPlus, FiTrash2, FiMoreVertical } from "react-icons/fi";

const QUESTION_TYPES = [
  { value: "short", label: "Short answer" },
  { value: "long", label: "Long answer" },
  { value: "yes_no", label: "Yes / No" },
];

const translations = {
  en: {
    title: "Application",
    subtitle: "Configure the fields and questions candidates will fill out when applying",
    field_label: "FIELD",
    visible_label: "VISIBLE",
    required_label: "REQUIRED",
    always_on: "Always on",
    add_custom_question: "Add custom question",
    save_error: "Error updating application form",
    save_success: "Changes saved",
  },
  fr: {
    title: "Candidature",
    subtitle: "Configurez les champs et questions que les candidats rempliront lors de leur candidature",
    field_label: "CHAMP",
    visible_label: "VISIBLE",
    required_label: "OBLIGATOIRE",
    always_on: "Toujours actif",
    add_custom_question: "Ajouter une question",
    save_error: "Erreur lors de la mise à jour du formulaire",
    save_success: "Modifications enregistrées",
  },
};

const Toggle = ({ checked, onChange, disabled = false, color = "blue" }) => {
  const bgColor = checked
    ? color === "orange" ? "bg-orange-400" : "bg-blue-500"
    : "bg-gray-200";

  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${bgColor} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? "translate-x-5" : "translate-x-0"}`}
      />
    </button>
  );
};

const FieldRow = ({ id, label, icon: Icon, value, onChange }) => {
  const field = (value || []).find((f) => f.id === id);
  const visible = field?.visible ?? false;
  const mandatory = field?.mandatory ?? false;
  const isDefault = field?.default ?? false;

  const handleToggleVisible = () => {
    if (isDefault) return;
    const newVisible = !visible;
    const arr = value || [];
    const exists = arr.some((f) => f.id === id);
    const updated = exists
      ? arr.map((f) => (f.id === id ? { ...f, visible: newVisible, mandatory: newVisible ? mandatory : false } : f))
      : [...arr, { id, visible: newVisible, mandatory: false }];
    onChange(updated);
  };

  const handleToggleMandatory = () => {
    if (isDefault) return;
    const arr = value || [];
    const exists = arr.some((f) => f.id === id);
    const newMandatory = !mandatory;
    const updated = exists
      ? arr.map((f) => (f.id === id ? { ...f, mandatory: newMandatory, visible: newMandatory ? true : f.visible } : f))
      : [...arr, { id, visible: true, mandatory: true }];
    onChange(updated);
  };

  return (
    <div className="grid grid-cols-12 gap-3 px-4 py-2.5 items-center hover:bg-gray-50 transition-colors">
      <div className="col-span-8 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-900">{label}</span>
      </div>
      <div className="col-span-2 flex justify-center">
        <Toggle checked={visible} onChange={handleToggleVisible} disabled={isDefault} />
      </div>
      <div className="col-span-2 flex justify-center">
        <Toggle checked={mandatory} onChange={handleToggleMandatory} disabled={isDefault} color="orange" />
      </div>
    </div>
  );
};

export default function Settings({ job, setJob }) {
  const { t } = useComponentTranslation(translations);
  const { id } = useParams();
  const [customQuestions, setCustomQuestions] = useState(job?.custom_questions || []);

  const handleFieldChange = useCallback(async (updatedFields) => {
    try {
      const response = await api.put(`/job/${id}`, { application_form_fields: updatedFields });
      if (!response.ok)    return toast.error(response.data.message);
      setJob(response.data);
      toast.success(t("save_success"));
    } catch (e) {
      console.error(e);
      toast.error(t("save_error"));
    }
  }, [id, setJob, t]);

  const saveQuestions = useCallback(async (updatedQuestions) => {
    try {
      const response = await api.put(`/job/${id}`, { custom_questions: updatedQuestions });
      if (!response.ok)    return toast.error(response.data.message);
      setJob(response.data);
      toast.success(t("save_success"));
    } catch (e) {
      console.error(e);
      toast.error(t("save_error"));
    }
  }, [id, setJob, t]);

  const addCustomQuestion = () => {
    const updated = [...customQuestions, { id: Date.now().toString(), question: "", type: "long", required: false }];
    setCustomQuestions(updated);
    saveQuestions(updated);
  };

  const updateCustomQuestion = (questionId, updates) => {
    setCustomQuestions(customQuestions.map((q) => (q.id === questionId ? { ...q, ...updates } : q)));
  };

  const deleteCustomQuestion = (questionId) => {
    const updated = customQuestions.filter((q) => q.id !== questionId);
    setCustomQuestions(updated);
    saveQuestions(updated);
  };

  return (
    <div className="max-w-3xl mx-auto py-6 space-y-4">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
        <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="col-span-8 text-xs font-medium text-gray-500 uppercase tracking-wider">{t("field_label")}</div>
          <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">{t("visible_label")}</div>
          <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">{t("required_label")}</div>
        </div>

        <div className="divide-y divide-gray-100">
          <FieldRow id="firstname" label="First Name" icon={FiUser} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="lastname" label="Last Name" icon={FiUser} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="email" label="Email" icon={FiMail} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="resume" label="Resume / CV" icon={FiFileText} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="linkedin" label="LinkedIn Profile" icon={FiLinkedin} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="cover_letter" label="Cover Letter" icon={FiMessageSquare} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="phone" label="Phone" icon={FiPhone} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="portfolio" label="Portfolio / Website" icon={FiGlobe} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="salary_expectations" label="Salary Expectations" icon={FiDollarSign} value={job?.application_form_fields} onChange={handleFieldChange} />
          <FieldRow id="availability" label="Available Start Date" icon={FiCalendar} value={job?.application_form_fields} onChange={handleFieldChange} />
        </div>
      </div>

      {/* Custom Questions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="space-y-3">
          {customQuestions.map((question) => (
            <div key={question.id} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
              <div className="pt-2 cursor-grab text-gray-300">
                <FiMoreVertical className="w-4 h-4" />
              </div>

              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={question.question}
                  onChange={(e) => updateCustomQuestion(question.id, { question: e.target.value })}
                  onBlur={(e) => {
                    const updated = customQuestions.map((q) => (q.id === question.id ? { ...q, question: e.target.value } : q));
                    saveQuestions(updated);
                  }}
                  placeholder="Enter your question..."
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />

                <div className="flex items-center gap-3">
                  <select
                    value={question.type}
                    onChange={(e) => {
                      updateCustomQuestion(question.id, { type: e.target.value });
                      saveQuestions(customQuestions.map((q) => (q.id === question.id ? { ...q, type: e.target.value } : q)));
                    }}
                    className="px-2 py-1 border border-gray-200 rounded text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {QUESTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>

                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={question.required}
                      onChange={(e) => {
                        updateCustomQuestion(question.id, { required: e.target.checked });
                        saveQuestions(customQuestions.map((q) => (q.id === question.id ? { ...q, required: e.target.checked } : q)));
                      }}
                      className="w-3.5 h-3.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                    />
                    Required
                  </label>
                </div>
              </div>

              <button
                onClick={() => deleteCustomQuestion(question.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            onClick={addCustomQuestion}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium text-xs transition-colors"
          >
            <FiPlus className="w-3.5 h-3.5" />
            {t("add_custom_question")}
          </button>
        </div>
      </div>
    </div>
  );
}
